import { isEmpty, map, pipe } from 'ramda';

import {
  buildCmsUrl,
  CMSApiBase,
  CMSGeneralButtonType,
  CMSGroupParams,
  CMSImageBase,
  CMSOrder,
  CMSScheduler,
  CMSToggle,
  getResponseCMS,
  LIMIT_500,
} from './base';
import { request } from '@/api/base';
import { getResponseCamelization } from '@/helpers/api';
import { renameKeys } from '@/helpers/common';
import { isCMSV3 } from '@/helpers/conf';
import { CurrencyCode } from '@/helpers/currency';

export enum BonusGroupMode {
  AND = 'AND',
  OR = 'OR',
}

export enum BonusType {
  ALL = 'all',
  CASINO = 'casino',
  SPORTS = 'sports',
}

export type UpTo = {
  currency: CurrencyCode;
  amount: number;
}[];

export interface BonusOrigin
  extends CMSApiBase,
    CMSGroupParams,
    CMSOrder,
    CMSToggle,
    CMSScheduler {
  title: string;
  offer: string | null;
  offerPercent: number | null;
  offerFreespins: number | null;
  upTo: UpTo | null;
  minDep: string | null;
  maxCashout: string | null;
  maxWinFs: string | null;
  wager: number | null;
  specialNote: string | null;
  code: string | null;
  promoText: string | null;
  buttonType: CMSGeneralButtonType;
  buttonLink: string | null;
  termsAndConditions: string;
  frequency: string | null;
  image?: CMSImageBase;
  typeOffer: BonusType;

  // workaround
  groupsInclude_: string | null;
  groupsExclude_: string | null;
}

export interface CMSBonus
  extends CMSApiBase,
    CMSGroupParams,
    CMSOrder,
    CMSToggle,
    CMSScheduler {
  title: string;
  offer: string | null;
  offerPercent: number | null;
  offerFreespins: number | null;
  upTo: UpTo | null;
  minDep: string | null;
  maxCashout: string | null;
  maxWinFs: string | null;
  wager: number | null;
  specialNote: string | null;
  code: string | null;
  promoText: string | null;
  buttonType: CMSGeneralButtonType;
  buttonLink: string | null;
  termsAndConditions: string;
  frequency: string | null;
  image?: CMSImageBase;
  identifier?: string;
  starsAnimation?: boolean;
  typeOffer: BonusType;
}

const normalizeBonuses = map<BonusOrigin, CMSBonus>((bonus) => {
  const groupsToRename: {
    [key: string]: string;
  } = {
    ...(!bonus.groupsExclude && { groupsExclude_: 'groupsExclude' }),
    ...(!bonus.groupsInclude && { groupsInclude_: 'groupsInclude' }),
  };

  if (!isEmpty(groupsToRename)) return renameKeys(groupsToRename)(bonus);

  return bonus;
});

export const bonusesCMSV3 = (lang: string) =>
  request<CMSBonus[], BonusOrigin[]>({
    url: buildCmsUrl({ path: 'bonuses-news', lang, limit: LIMIT_500 }),
    transformResponse: pipe(getResponseCamelization, normalizeBonuses),
  });

export const bonusesCMSV4 = (lang: string) =>
  request<CMSBonus[], BonusOrigin[]>({
    url: buildCmsUrl({
      path: 'bonuses-news',
      lang,
      limit: LIMIT_500,
      additionalParams: { populate: 'deep' },
    }),
    transformResponse: pipe(
      getResponseCamelization,
      getResponseCMS,
      normalizeBonuses
    ),
  });

export const bonusesCMS = isCMSV3 ? bonusesCMSV3 : bonusesCMSV4;
